import { getRequest } from "./ApiService";

let _cachedData = null;
let _cacheTime = 0;
let _cacheKey = null;
// In-flight promise + key. Second (and third, ...) callers with the
// same key while a request is still in flight get the same promise
// instead of firing another network request. Prevents the "3x general-
// info" bursts we saw on page refresh when App.js, the onboarding
// gate, and round-admin all fired at once.
let _inflight = null;
let _inflightKey = null;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

// The backend has two different general-info responses keyed by
// `position=instructor` vs `position=student`. Always pass the
// caller's position so we hit the right handler.
//
// `scope` is an optional payload-trim hint honored by BE (e.g.
// `scope=dashboard` returns the minimal field set needed by the
// instructor dashboard). Scope is part of the cache key so a trimmed
// dashboard call and an untrimmed call from another page don't
// collide.
export function fetchAppGeneralInfo(gameId, position, scope) {
  const now = Date.now();
  const key = `${position || ""}|${gameId || 0}|${scope || ""}`;
  if (_cachedData && _cacheKey === key && (now - _cacheTime) < CACHE_TTL) {
    return Promise.resolve(_cachedData);
  }
  if (_inflight && _inflightKey === key) {
    return _inflight;
  }

  const base = `${process.env.REACT_APP_API_URL}app/general-info/`;
  const params = [];
  if (position) params.push(`position=${position}`);
  if (gameId) params.push(`game_id=${gameId}`);
  if (scope) params.push(`scope=${scope}`);
  const url = params.length ? `${base}?${params.join('&')}` : base;

  _inflightKey = key;
  _inflight = getRequest(url).then(({ body }) => {
    _cachedData = body;
    _cacheTime = now;
    _cacheKey = key;
    _inflight = null;
    _inflightKey = null;
    return body;
  }).catch(err => {
    // Clear in-flight on failure so retries aren't stuck on a
    // permanently-rejected promise.
    _inflight = null;
    _inflightKey = null;
    throw err;
  });
  return _inflight;
}

export function clearAppGeneralInfoCache() {
  _cachedData = null;
  _cacheTime = 0;
  _cacheKey = null;
  _inflight = null;
  _inflightKey = null;
}

// Deterministic active-course/active-game picker. Multi-course users
// have arrays of courses and games in every general-info / login
// payload. Rule: pick the highest-id course, then pick the highest-id
// game whose game.course matches the picked course. This prevents the
// "highest-id game across all games" fallback from silently promoting
// a foreign course's game (the leak that motivated adding this).
export function pickCourseAndGame(courses, games) {
  const courseList = Array.isArray(courses) ? courses : [];
  const gameList = Array.isArray(games) ? games : [];
  if (courseList.length === 0) {
    return { course: null, game: null };
  }
  const pickedCourse = [...courseList].sort((a, b) => b.id - a.id)[0];
  const gamesInCourse = gameList.filter(
    g => Number(g.course) === Number(pickedCourse.id)
  );
  const pickedGame = gamesInCourse.length > 0
    ? [...gamesInCourse].sort((a, b) => b.id - a.id)[0]
    : null;
  return { course: pickedCourse, game: pickedGame };
}



// WEBPACK FOOTER //
// ./src/api/AppGeneralInfoService.js